import type { JSX } from 'react';

import { translationKey } from '@change-corgi/core/i18n';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { ErrorState, ListHeading, LoadingState } from '../../shared/components';
import type { SuggestedPetitionSourceLocation } from '../../shared/types';

import { NewlyCreatedPetitionsList } from './components/NewlyCreatedPetitionsList';
import { useNewlyCreatedPetitions } from './hooks/useNewlyCreatedPetitions';

type Props = {
	petitionSlugOrId?: string;
	sourceLocation: SuggestedPetitionSourceLocation;
};

export function NewlyCreatedPetitions({ petitionSlugOrId, sourceLocation }: Props): JSX.Element | null {
	const {
		data: { petitionsState },
	} = useNewlyCreatedPetitions({ petitionSlugOrId });

	if (isLoading(petitionsState)) {
		return (
			<div data-nosnippet>
				<ListHeading title={translationKey('corgi.petition.details.newly_created_petitions.header')} />
				<LoadingState />
			</div>
		);
	}

	if (isError(petitionsState)) {
		return (
			<div data-nosnippet>
				<ListHeading title={translationKey('corgi.petition.details.newly_created_petitions.header')} />
				<ErrorState />
			</div>
		);
	}

	if (petitionsState.petitions.length === 0) return null;

	return (
		// https://iloveseo.com/seo/use-data-nosnippet-attribute-to-remove-text-in-google-serp-snippet/
		<div data-nosnippet>
			<ListHeading title={translationKey('corgi.petition.details.newly_created_petitions.header')} />
			<NewlyCreatedPetitionsList
				petitions={petitionsState.petitions}
				parentPetitionId={petitionsState.parentPetitionId}
				sourceLocation={sourceLocation}
			/>
		</div>
	);
}
